// ============================================================================
// VENTES & FISCAL › OBJECTIFS — le budget HT du magasin, mois par mois, et la façon dont chaque mois se répartit sur ses
// jours : le poids de chaque jour de semaine, les jours exceptionnels. Réf. maquette du back-office (écran `vobj`) et
// docs/CADRAGE-Statistiques-Ventes.md §11.
//
// ⚠️ LE RÉALISÉ N'EST JAMAIS SAISI : il vient des ventes, et un mois à venir n'a pas de taux d'atteinte (« — »), pas 0 %.
// ⚠️ CHAQUE GESTE S'ENREGISTRE DÈS QU'IL EST FAIT (sortie du champ, bouton), et laisse sa trace à l'historique : le serveur
//    écrit le geste et sa trace ensemble (`sales/objectives-write.ts`).
// ⚠️ UN SEUL SYSTÈME, PAS DEUX MODES : chaque jour reçoit sa part du mois, et taper un montant dans le calendrier d'un mois
//    FIXE ce jour — le reste du mois va aux autres. Tous les jours fixés, le mois est leur somme. Les montants affichés sont
//    ceux du serveur : l'écran ne refait jamais la répartition.
// ⚠️ CE QUE LA MAQUETTE DESSINE ET QUI N'EST PAS CONSTRUIT N'EST PAS POSÉ (§23.0) : le détail par rayon (l'onglet « Par
//    famille » du mois ouvert) ou par vendeur et l'affichage en caisse arrivent avec leurs tranches (CONVENTIONS-PRODUCTION
//    du back-office).
// ============================================================================
import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, Icon, Tooltip, Trans, confirm, showToast } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { formatMoney, parseMinor, toInputValue, currentCurrency } from '@orgazone/money-format';
import { rescale } from '@orgazone/core-money';
import { dateTime, date as formatDate } from '@orgazone/date-format';
import { parseSignedPercentBp } from '@orgazone/core-analytics';
import { ApiError } from '@orgazone/api-envelope/client';
import { api, type ObjectiveChangeView, type ObjectiveDayView, type ObjectiveMonthView, type ObjectiveYearView } from '../api';
import { key, t, type Translatable } from '../i18n';
import { currentLang } from '../lang';
import { moisLongLisible } from '../periode';
import { useStoreScope } from '../store-context';
import { exporterCsv } from './export-csv';
import { csvRate, dTx, eur0 } from './stats-format';
import { Seg } from './stats-resume';

/** Les mots d'un refus de saisie, par CODE de la règle (`core-analytics/objectives`) — dans la langue du compte. */
const REFUSALS: Record<string, Translatable> = {
  amount_not_integer: key('Un objectif est un montant.'),
  amount_not_positive: key('Un objectif est un montant positif — videz le champ pour retirer celui du mois.'),
  amount_too_large: key('Ce montant est hors de toute mesure.'),
  weights_count: key('Les sept jours se saisissent ensemble.'),
  weight_out_of_range: key('Un poids va de 0 à 400.'),
  day_invalid: key('Cette date n’existe pas.'),
  kind_unknown: key('Une fermeture ou un événement, rien d’autre.'),
  change_required: key('Un événement porte sa variation.'),
  change_out_of_range: key('Une variation va de −99 % à +300 %.'),
  change_zero: key('Une variation nulle ne change rien.'),
  change_on_closure: key('Une fermeture ne porte pas de variation.'),
  month_invalid: key('Ce mois n’existe pas.'),
  year_invalid: key('Cet exercice n’existe pas.'),
  month_from_days: key('Tous les jours de ce mois sont fixés : son objectif est leur somme. Rendez un jour au calcul pour saisir le mois.'),
  month_below_fixed: key('Les jours fixés de ce mois dépassent ce montant : baissez-les, ou saisissez un objectif plus haut.'),
  days_over_month: key('Les jours fixés dépasseraient l’objectif du mois : il ne reste pas assez pour ce jour.'),
  change_on_fixed: key('Un jour fixé porte un montant, pas une variation.'),
  amount_on_exception: key('Une fermeture ou un événement ne porte pas de montant.'),
};

/** Le message d'un échec : le refus de la règle dans la langue du compte, sinon le motif réel. */
function failureText(e: unknown): string {
  const code = e instanceof ApiError ? e.errors[0]?.code : undefined;
  const refusal = code ? REFUSALS[code] : undefined;
  return refusal ? t(refusal) : e instanceof Error ? e.message : String(e);
}

/** Un mot en tête de cellule : la majuscule ajoutée — `Intl` rend « janvier », juste au milieu d'une phrase. */
const capital = (s: string): string => s.charAt(0).toLocaleUpperCase(currentLang()) + s.slice(1);

/** Le nom d'un mois seul (« Janvier »), en tête de ligne. */
const monthLabel = (m: string): string =>
  capital(new Intl.DateTimeFormat(currentLang(), { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(4, 6)) - 1, 1))));

/** Le nom d'un jour de semaine, 1 = lundi … 7 = dimanche (le 5 janvier 2026 est un lundi). */
const weekdayLabel = (wd: number): string =>
  capital(new Intl.DateTimeFormat(currentLang(), { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 4 + wd))));

/** Le jour de semaine abrégé, en tête de colonne du calendrier (« lun. »), 1 = lundi … 7 = dimanche. */
const weekdayShort = (wd: number): string =>
  new Intl.DateTimeFormat(currentLang(), { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 4 + wd)));

/** `AAAAMMJJ` → une date UTC, pour la nommer sans dépendre du fuseau du navigateur. */
const utcOfYmd = (ymd: string): Date => new Date(Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8))));

/** La colonne d'un jour dans le calendrier : 1 = lundi … 7 = dimanche. */
const weekdayOfYmd = (ymd: string): number => ((utcOfYmd(ymd).getUTCDay() + 6) % 7) + 1;

/** Le jour nommé dans l'infobulle de sa case (« Samedi 19 »). */
const dayName = (ymd: string): string =>
  capital(new Intl.DateTimeFormat(currentLang(), { weekday: 'long', day: 'numeric', timeZone: 'UTC' }).format(utcOfYmd(ymd)));

/** `AAAAMMJJ` → une date locale. */
const dateOfYmd = (ymd: string): Date => new Date(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)));

/**
 * Un objectif dans son champ : des unités entières quand il en est un (« 47000 »), ses décimales sinon.
 * ⚠️ SANS SÉPARATEUR DE MILLIERS : le texte du champ doit se RELIRE à l'identique (`parseMinor`), quelle que soit la devise.
 */
function amountInput(cents: number | null): string {
  if (cents === null) return '';
  const scale = currentCurrency().scale;
  return cents % 10 ** scale === 0 ? toInputValue(rescale(cents, scale, 0), 0) : toInputValue(cents);
}

/**
 * Un montant en unités entières, groupé et sans symbole (« 1 980 ») — la part d'un jour en filigrane, la somme d'un mois.
 * ⚠️ JAMAIS DANS LA VALEUR D'UN CHAMP : groupé, le texte ne se relirait pas (`amountInput`).
 */
const grouped = (cents: number): string => formatMoney(cents, { displayScale: 0, symbol: '' });

/** La couleur d'une atteinte : vert à 100 %, ambre à partir de 90 %, rose en dessous. */
const rateInk = (bp: number | null): string => (bp === null ? 'var(--ink-300)' : bp >= 10_000 ? 'var(--teal-800)' : bp >= 9_000 ? 'var(--amber-fg)' : 'var(--rose-fg)');
const rateBar = (bp: number | null): string => (bp === null ? 'transparent' : bp >= 10_000 ? 'var(--teal-500)' : bp >= 9_000 ? 'var(--amber)' : 'var(--rose)');
/** Un taux entier (« 94 % ») — ce que la colonne d'atteinte affiche. */
const pct0 = (bp: number | null): string => (bp === null ? '—' : `${Math.round(bp / 100)} %`);

const MONO: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' };
const GRID = 'minmax(96px,1.1fr) 116px minmax(104px,1.3fr) 96px 132px';
const FIELD: CSSProperties = {
  height: 32, padding: '0 9px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-body)',
  color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums',
};
const CARD: CSSProperties = {
  background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0,
};
/** Les hachures d'un jour fermé — sa case, et sa pastille dans la légende. */
const HATCH = 'repeating-linear-gradient(135deg, var(--ink-50) 0 6px, var(--paper) 6px 12px)';
const PILL: CSSProperties = {
  height: 17, padding: '0 6px', borderRadius: 'var(--r-pill)', fontSize: 'var(--t-micro)', fontWeight: 800, display: 'inline-flex', alignItems: 'center',
  flexShrink: 0, whiteSpace: 'nowrap',
};
const BUTTON: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'none',
};

/** L'en-tête d'une carte : son pictogramme teinté, son titre, ce qu'elle fait. */
function CardHead({ icon, bg, fg, title, desc }: { icon: IconName; bg: string; fg: string; title: string; desc: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <span style={{ width: 30, height: 30, borderRadius: 'var(--r-sm)', background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={icon} size={15} stroke={1.9} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{title}</span>
        <span style={{ display: 'block', marginTop: 2, fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.45 }}>{desc}</span>
      </span>
    </div>
  );
}

/**
 * UN CHAMP QUI S'ENREGISTRE EN SORTANT — le texte tapé reste le sien tant qu'on écrit, l'enregistrement part à la sortie
 * du champ, et seulement s'il a changé.
 * ⚠️ UN REFUS REND LE CHAMP À SA VALEUR ENREGISTRÉE : garder la saisie refusée laisserait croire qu'elle tient.
 */
function CommitField({ value, onCommit, label, style, inputMode = 'numeric', placeholder, readOnly = false, title, className }: {
  value: string; onCommit: (text: string) => Promise<boolean>; label: string; style: CSSProperties; inputMode?: 'numeric' | 'decimal'; placeholder?: string;
  readOnly?: boolean; title?: string; className?: string;
}) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  return (
    <input value={text} aria-label={label} inputMode={inputMode} placeholder={placeholder} style={style} readOnly={readOnly} title={title}
      className={className}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setText(value); } }}
      onBlur={() => {
        if (text.trim() === value.trim()) return;
        void onCommit(text).then((kept) => { if (!kept) setText(value); });
      }} />
  );
}

/**
 * La phrase d'un geste sur les objectifs — composée dans la langue du compte, d'après les faits que le serveur a gardés.
 * ⚠️ EXPORTÉE : le journal d'activité affiche les mêmes gestes, et deux phrases du même geste finiraient par diverger.
 */
export function objectiveChangeText(h: ObjectiveChangeView): string {
  const money = (c: number | null) => (c === null ? t('vide') : eur0(c));
  const day = (d: string | null) => (d ? formatDate(dateOfYmd(d)) : '');
  switch (h.kind) {
    case 'month': return t('Objectif {mois} : {avant} → {apres}', { mois: h.subject ? moisLongLisible(h.subject) : '', avant: money(h.beforeCents), apres: money(h.afterCents) });
    case 'prefill': return t('Exercice {annee} pré-rempli : réalisé {source} {variation} ({nombre})', {
      annee: h.subject ?? '', source: String(Number(h.subject) - 1), variation: dTx(h.changeBp), nombre: t('{n} mois', { n: h.count ?? 0 }),
    });
    case 'copy': return t('Objectifs {source} recopiés sur {annee} ({nombre})', { source: String(Number(h.subject) - 1), annee: h.subject ?? '', nombre: t('{n} mois', { n: h.count ?? 0 }) });
    case 'weights': return t('Poids des jours de semaine modifiés');
    case 'weights_reset': return t('Poids des jours : retour au profil réel');
    case 'day_added': return h.dayKind === 'closed'
      ? t('Fermeture exceptionnelle le {date}', { date: day(h.subject) })
      : t('Événement {variation} le {date}', { variation: dTx(h.changeBp), date: day(h.subject) });
    case 'day_removed': return t('Jour exceptionnel retiré : {date}', { date: day(h.subject) });
    case 'day_fixed': return h.beforeCents === null
      ? t('Objectif du {date} fixé à {montant}', { date: day(h.subject), montant: money(h.afterCents) })
      : t('Objectif du {date} : {avant} → {apres}', { date: day(h.subject), avant: money(h.beforeCents), apres: money(h.afterCents) });
    case 'day_unfixed': return t('Objectif du {date} rendu au calcul (il était fixé à {montant})', { date: day(h.subject), montant: money(h.beforeCents) });
    case 'month_from_days': return t('{mois} devient la somme de ses jours fixés : {avant} → {apres}', {
      mois: h.subject ? capital(moisLongLisible(h.subject)) : '', avant: money(h.beforeCents), apres: money(h.afterCents),
    });
    case 'days_unfixed': return t('{mois} : {n} jour rendu au calcul', { mois: h.subject ? capital(moisLongLisible(h.subject)) : '', n: h.count ?? 0 });
    default: return h.kind;
  }
}

/**
 * Ce que dit l'en-tête de la grille des jours d'un mois — d'après les chiffres du serveur.
 * ⚠️ AUCUN MONTANT N'EST CALCULÉ ICI : le reste, la somme des jours fixés, l'écart viennent tous de la lecture de l'exercice.
 */
function daysSummary(m: ObjectiveMonthView): string {
  if (m.fromDays) return t('Tous les jours sont fixés : l’objectif du mois est leur somme, {montant}.', { montant: eur0(m.objectiveCents) });
  if (m.overCents !== null) return t('Les jours fixés dépassent l’objectif du mois de {montant} : les autres jours n’ont pas de cible.', { montant: eur0(m.overCents) });
  if (m.objectiveCents === null) {
    return m.fixedDays
      ? t('Pas d’objectif mensuel : seuls les jours fixés ont une cible, {montant} en tout.', { montant: eur0(m.fixedCents) })
      : t('Aucun objectif ce mois : saisissez le mois, ou fixez des jours un à un.');
  }
  if (m.freeCents === null) return t('Ce mois n’a aucun jour ouvert où poser son objectif.');
  return m.fixedDays
    ? t('{n} jour fixé pour {fixe} — il reste {reste} pour les autres jours, selon leur poids.', { n: m.fixedDays, fixe: eur0(m.fixedCents), reste: eur0(m.freeCents) })
    : t('Chaque jour reçoit sa part du mois selon son poids. Tapez un montant pour fixer un jour.');
}

/**
 * UNE CASE DU CALENDRIER — sa part du mois en filigrane, ou son montant fixé en gras.
 * ⚠️ TAPER UN MONTANT FIXE LE JOUR, VIDER LE CHAMP OU « fixé ✕ » LE RENDENT AU CALCUL : pas de bouton « fixer », le geste
 *    est la saisie elle-même. Un jour fermé se fixe aussi : c'est décider qu'il ouvre.
 * ⚠️ « fixé ✕ » GARDE SES 22 PX SUR TABLETTE : la règle tactile des 44 px ne vit que dans les tableaux (`.dt-root`), et
 *    elle gonflerait ici toute la rangée du calendrier — sa taille est sa fonction.
 */
function CalendarDay({ d, isToday, exceptional, busy, onCommit, onUnfix }: {
  d: ObjectiveDayView; isToday: boolean; exceptional: boolean; busy: boolean; onCommit: (text: string) => Promise<boolean>; onUnfix: () => void;
}) {
  const shown = formatDate(dateOfYmd(d.day));
  const name = dayName(d.day);
  const tip = d.fixed ? t('{jour} · fixé à {montant} HT', { jour: name, montant: eur0(d.objectiveCents) })
    : d.closed ? t('{jour} · fermé — tapez un montant pour l’ouvrir', { jour: name })
      : d.objectiveCents === null ? t('{jour} · aucune cible', { jour: name })
        : t('{jour} · part calculée {montant} HT', { jour: name, montant: eur0(d.objectiveCents) });
  const placeholder = d.closed ? t('fermé') : d.objectiveCents === null ? '—' : grouped(d.objectiveCents);
  return (
    <div data-day={d.day} style={{
      display: 'flex', flexDirection: 'column', gap: 6, padding: 7, minWidth: 0, borderRadius: 'var(--r-sm)',
      border: `1px solid ${d.fixed ? 'var(--teal-400)' : 'var(--ink-200)'}`,
      background: d.fixed ? 'var(--teal-50)' : d.closed ? HATCH : 'var(--paper)',
      boxShadow: isToday ? '0 0 0 2px var(--teal-500)' : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, minHeight: 20, minWidth: 0 }}>
        <Tooltip content={tip}>
          <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: d.closed ? 'var(--ink-400)' : 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
            {Number(d.day.slice(6, 8))}
          </span>
        </Tooltip>
        {d.fixed ? (
          <button type="button" disabled={busy} onClick={onUnfix} className="oz-obj-unfix" aria-label={t('Rendre le {date} au calcul', { date: shown })}
            style={{ marginLeft: 'auto', height: 22, padding: '0 6px 0 8px', border: 'none', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-micro)', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0, cursor: 'pointer' }}>
            {t('fixé')}<Icon name="x" size={10} stroke={2.6} />
          </button>
        ) : d.changeBp !== null ? (
          <span style={{ ...PILL, background: 'var(--violet-bg)', color: 'var(--violet-fg)' }}>{dTx(d.changeBp)}</span>
        ) : d.closed ? (
          <span style={{ ...PILL, background: 'var(--ink-100)', color: 'var(--ink-500)' }}>{exceptional ? t('fermeture') : t('fermé')}</span>
        ) : null}
      </div>
      <CommitField value={d.fixed ? amountInput(d.objectiveCents) : ''} onCommit={onCommit} placeholder={placeholder}
        label={t('Objectif du {date}', { date: shown })} className="oz-obj-day"
        style={{
          ...FIELD, width: '100%', height: 30, padding: '0 7px', textAlign: 'right', fontWeight: d.fixed ? 800 : 600,
          ...(d.fixed ? { border: '1px solid var(--teal-400)', color: 'var(--teal-800)' } : {}),
        }} />
    </div>
  );
}

/** Un chiffre de l'en-tête du calendrier : son libellé au-dessus, sa valeur. */
function Figure({ label, value, ink }: { label: string; value: string; ink: string }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <span style={MONO}>{label}</span>
      <span style={{ fontSize: 'var(--t-strong)', fontWeight: 800, color: ink, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</span>
    </span>
  );
}

/** Une pastille de la légende du calendrier. */
function Swatch({ label, style }: { label: string; style: CSSProperties }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
      <span aria-hidden style={{ width: 14, height: 10, borderRadius: 2, boxSizing: 'border-box', flexShrink: 0, ...style }} />
      {label}
    </span>
  );
}

/**
 * LE CALENDRIER D'UN MOIS OUVERT — ce que dit le mois (sa phrase, trois chiffres, une barre), une case par jour, lundi
 * d'abord, puis la légende et le geste qui rend tout le mois au calcul.
 * ⚠️ AUCUN MONTANT N'EST CALCULÉ ICI : chiffres, barre et parts viennent de la lecture de l'exercice — la barre ne fait
 *    que poser côte à côte les deux montants que le serveur a rendus, sur la plus grande des deux bases.
 */
function MonthCalendar({ m, view, busy, onCommit, onUnfix, onUnfixMonth }: {
  m: ObjectiveMonthView; view: ObjectiveYearView; busy: boolean; onCommit: (day: string) => (text: string) => Promise<boolean>;
  onUnfix: (day: string) => void; onUnfixMonth: () => void;
}) {
  const over = m.overCents !== null;
  const base = Math.max(m.objectiveCents ?? 0, m.fixedCents);
  const share = (cents: number): string => (base > 0 ? `${(cents / base) * 100}%` : '0%');
  const lead = m.days[0] ? weekdayOfYmd(m.days[0].day) - 1 : 0;
  const trail = (7 - ((lead + m.days.length) % 7)) % 7;
  // Une fermeture EXCEPTIONNELLE se dit « fermeture » ; un jour que le magasin n'ouvre jamais, « fermé ».
  const closures = new Set(view.exceptions.filter((x) => x.kind === 'closed').map((x) => x.day));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px 16px', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 240, fontSize: 'var(--t-body)', fontWeight: 600, color: over ? 'var(--rose-fg)' : 'var(--ink-700)', lineHeight: 1.5 }}>{daysSummary(m)}</span>
          <span style={{ display: 'flex', gap: '8px 20px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Figure label={t('Objectif du mois')} value={eur0(m.objectiveCents)} ink="var(--ink-900)" />
            <Figure label={t('Jours fixés')} ink={over ? 'var(--rose-fg)' : 'var(--teal-800)'}
              value={m.fixedDays ? t('{montant} · {n} j', { montant: eur0(m.fixedCents), n: m.fixedDays }) : '—'} />
            <Figure label={t('Réparti selon le poids')} value={eur0(m.freeCents)} ink="var(--ink-700)" />
          </span>
        </div>
        <div aria-hidden style={{ display: 'flex', height: 6, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
          <span style={{ width: share(m.fixedCents), background: over ? 'var(--rose)' : 'var(--teal-700)', transition: 'width .3s' }} />
          <span style={{ width: share(m.freeCents ?? 0), background: 'var(--teal-400)', transition: 'width .3s' }} />
        </div>
      </div>

      {/* ⚠️ LA BARRE DE DÉFILEMENT N'APPARAÎT QUE SI LA GRILLE NE TIENT VRAIMENT PAS : aucun retrait horizontal autour d'elle.
          Les 2 px de la grille laissent passer l'anneau du jour, que le défilement rognerait — en bas aussi, pour un
          aujourd'hui dans la dernière semaine. */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: 6, minWidth: 556, boxSizing: 'border-box', padding: '0 2px 2px' }}>
          {[1, 2, 3, 4, 5, 6, 7].map((wd) => {
            const open = view.openDays.includes(wd);
            return (
              <span key={`wd-${wd}`} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0 4px', padding: '0 2px', minWidth: 0 }}>
                <span style={MONO}>{weekdayShort(wd)}</span>
                {open
                  ? <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{t('poids')} {view.weights.values[wd - 1] ?? 0}</span>
                  : <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-300)', whiteSpace: 'nowrap' }}>{t('fermé')}</span>}
              </span>
            );
          })}
          {Array.from({ length: lead }, (_, i) => <span key={`lead-${i}`} aria-hidden />)}
          {m.days.map((d) => (
            <CalendarDay key={d.day} d={d} isToday={d.day === view.today} exceptional={closures.has(d.day)} busy={busy}
              onCommit={onCommit(d.day)} onUnfix={() => onUnfix(d.day)} />
          ))}
          {Array.from({ length: trail }, (_, i) => <span key={`trail-${i}`} aria-hidden />)}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px 14px', flexWrap: 'wrap', fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
        <Swatch label={t('Part calculée')} style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)' }} />
        <Swatch label={t('Fixé à la main')} style={{ background: 'var(--teal-50)', border: '1px solid var(--teal-400)' }} />
        <Swatch label={t('Événement')} style={{ background: 'var(--violet-bg)' }} />
        <Swatch label={t('Fermé')} style={{ background: HATCH, border: '1px solid var(--ink-200)' }} />
        <span style={{ flex: '1 1 260px', minWidth: 0 }}>
          {t('Tapez un montant pour fixer un jour : le reste du mois se répartit sur les autres. Videz-le, ou cliquez « fixé ✕ », pour le rendre au calcul.')}
        </span>
        {m.fixedDays > 0 && (
          <button type="button" disabled={busy} onClick={onUnfixMonth} className="oz-obj-reset"
            style={{ ...BUTTON, height: 30, padding: '0 11px', fontSize: 'var(--t-dense)', marginLeft: 'auto' }}>
            {t('Rendre tout le mois au calcul')}
          </button>
        )}
      </div>
    </div>
  );
}

/** Le motif d'une lecture qui n'a pas abouti — aucun chiffre ne s'affiche. */
function ReadFailed({ message }: { message: string }) {
  return (
    <div className="dcard" role="alert" style={{ padding: 24 }}>
      <div style={{ fontSize: 'var(--t-h4)', fontWeight: 600, color: 'var(--danger-ink)', marginBottom: 6 }}>{t('Objectifs illisibles')}</div>
      <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>{t('La lecture n’a pas abouti : {message}.', { message })}</p>
    </div>
  );
}

/** L'écran de saisie des objectifs du magasin actif. */
export function Objectifs() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  // ⚠️ L'EXERCICE DEMANDÉ, `null` à l'ouverture : le serveur rend alors l'année en cours DU MAGASIN. Retenir l'année reçue ici
  //    relancerait la lecture qui vient de la rendre.
  const [year, setYear] = useState<number | null>(null);
  const [view, setView] = useState<ObjectiveYearView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [prefillText, setPrefillText] = useState('5');
  const [exDay, setExDay] = useState('');
  const [exKind, setExKind] = useState<'event' | 'closed'>('event');
  const [exText, setExText] = useState('30');
  const [busy, setBusy] = useState(false);
  // Le mois dont la grille des jours est ouverte — un seul à la fois, comme le détail de la maquette.
  const [openMonth, setOpenMonth] = useState<string | null>(null);

  useEffect(() => {
    if (!gln) return;
    let alive = true;
    api.objectivesYear(year ?? undefined)
      .then((v) => { if (!alive) return; setView(v); setFailure(null); })
      .catch((e: unknown) => { if (!alive) return; setView(null); setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [gln, year, tick]);

  const reload = () => setTick((n) => n + 1);

  /** Un geste : l'appel, puis l'exercice relu — l'écran montre ce que le serveur a enregistré, pas ce qu'on a tapé. */
  async function gesture(run: () => Promise<{ changed: boolean }>, done: string): Promise<boolean> {
    try {
      const r = await run();
      if (r.changed) showToast(done, 'ok');
      reload();
      return true;
    } catch (e) {
      showToast(failureText(e), 'err');
      return false;
    }
  }

  if (!gln) return <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour saisir ses objectifs.')} /></div>;

  const y = view?.year ?? null;
  const commitMonth = (month: string) => async (text: string): Promise<boolean> => {
    const parsed = parseMinor(text);
    if (parsed.state === 'rejected') { showToast(parsed.reason, 'err'); return false; }
    const cents = parsed.state === 'empty' ? null : parsed.minor;
    return gesture(() => api.setMonthObjective(month, cents), t('Objectif de {mois} enregistré', { mois: moisLongLisible(month) }));
  };
  const commitDay = (day: string) => async (text: string): Promise<boolean> => {
    const parsed = parseMinor(text);
    if (parsed.state === 'rejected') { showToast(parsed.reason, 'err'); return false; }
    const date = formatDate(dateOfYmd(day));
    if (parsed.state === 'empty') return gesture(() => api.removeObjectiveDay(day), t('Le {date} revient au calcul', { date }));
    return gesture(() => api.fixObjectiveDay(day, parsed.minor), t('Objectif du {date} fixé', { date }));
  };
  const unfixDay = (day: string) => {
    void gesture(() => api.removeObjectiveDay(day), t('Le {date} revient au calcul', { date: formatDate(dateOfYmd(day)) }));
  };
  /**
   * Rend au calcul tous les jours fixés d'un mois, après confirmation. Le compte rendu dit les jours que le SERVEUR a rendus,
   * pas ceux que l'écran croyait fixés : une autre session a pu en rendre entre-temps.
   */
  const unfixMonth = async (m: ObjectiveMonthView) => {
    const mois = moisLongLisible(m.month);
    const ok = await confirm({
      title: t('Rendre {n} jour de {mois} au calcul ?', { n: m.fixedDays, mois }),
      message: t('Les montants fixés à la main sont retirés : chaque jour reprend sa part du mois selon son poids. L’objectif du mois ne change pas.'),
      confirmLabel: t('Rendre au calcul'), tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.unfixMonthDays(m.month);
      if (r.changed) showToast(t('{mois} : {n} jour rendu au calcul', { mois: capital(mois), n: r.written ?? 0 }), 'ok');
      else showToast(t('Aucun jour fixé en {mois} : rien à rendre au calcul.', { mois }), 'info');
      reload();
    } catch (e) { showToast(failureText(e), 'err'); } finally { setBusy(false); }
  };
  const commitWeight = (index: number) => async (text: string): Promise<boolean> => {
    if (!view) return false;
    const v = Number(text.trim());
    if (!/^\d{1,3}$/.test(text.trim())) { showToast(t(REFUSALS.weight_out_of_range!), 'err'); return false; }
    const next = view.weights.values.map((w, i) => (i === index ? v : w));
    return gesture(() => api.setObjectiveWeights(next), t('Poids des jours enregistrés'));
  };

  const prefill = async () => {
    if (!y) return;
    const bp = parseSignedPercentBp(prefillText);
    if (bp === null) { showToast(t('Une progression s’écrit en pour cent (ex. 5 ou -2,5).'), 'err'); return; }
    const ok = await confirm({
      title: t('Pré-remplir {annee} depuis {source} {variation} ?', { annee: String(y), source: String(y - 1), variation: dTx(bp) }),
      message: t('Chaque mois reçoit le réalisé HT des mêmes jours de semaine 52 semaines plus tôt (en {source}), majoré de {variation} et arrondi à la centaine ; un mois sans vente reste tel quel. Vous pourrez ensuite ajuster chaque mois.', { source: String(y - 1), variation: dTx(bp) }),
      confirmLabel: t('Pré-remplir'), tone: 'info',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.prefillObjectives(y, bp);
      // ⚠️ DEUX PHRASES ENTIÈRES selon qu'un mois a été laissé ou non : une phrase faite de deux morceaux comptés ne se
      //    traduit pas, chaque langue place ses nombres où sa grammaire le veut.
      if (r.changed) {
        showToast(r.skipped ? t('{n} mois pré-rempli — les autres n’ont pas vendu l’an dernier et restent tels quels.', { n: r.written ?? 0 })
          : t('{n} mois pré-rempli', { n: r.written ?? 0 }), 'ok');
      } else if (!r.kept) showToast(t('Aucune vente en {source} : rien à pré-remplir.', { source: String(y - 1) }), 'info');
      if (r.kept) showToast(t('{n} mois gardé tel quel : ses jours fixés décident de son objectif.', { n: r.kept }), 'info');
      reload();
    } catch (e) { showToast(failureText(e), 'err'); } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!y) return;
    const ok = await confirm({
      title: t('Recopier les objectifs de {source} ?', { source: String(y - 1) }),
      message: t('Chaque mois de {annee} reprend l’objectif du même mois de {source} ; un mois sans objectif en {source} garde le sien.', { annee: String(y), source: String(y - 1) }),
      confirmLabel: t('Recopier'), tone: 'info',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.copyObjectives(y);
      if (r.changed) showToast(t('{n} mois recopié', { n: r.written ?? 0 }), 'ok');
      else if (!r.kept) showToast(t('Aucun objectif en {source} à recopier.', { source: String(y - 1) }), 'info');
      if (r.kept) showToast(t('{n} mois gardé tel quel : ses jours fixés décident de son objectif.', { n: r.kept }), 'info');
      reload();
    } catch (e) { showToast(failureText(e), 'err'); } finally { setBusy(false); }
  };

  const addException = async () => {
    if (!exDay) { showToast(t('Choisissez la date.'), 'err'); return; }
    const bp = exKind === 'event' ? parseSignedPercentBp(exText) : null;
    if (exKind === 'event' && bp === null) { showToast(t('Une variation s’écrit en pour cent (ex. 30 ou -20).'), 'err'); return; }
    const kept = await gesture(() => api.setObjectiveDay(exDay.replace(/-/g, ''), exKind, bp), t('Jour exceptionnel enregistré'));
    if (kept) setExDay('');
  };

  const exportYear = () => {
    if (!view) return;
    const code = currentCurrency().code;
    exporterCsv(`objectifs-${view.year}`, [t('Mois'), t('Objectif HT ({code})', { code }), t('Réalisé HT ({code})', { code }), t('Atteinte (%)')],
      view.months.map((m) => [moisLongLisible(m.month), toInputValue(m.objectiveCents), toInputValue(m.realizedHtCents), csvRate(m.rateBp)]));
  };

  const maxWeight = Math.max(1, ...(view?.weights.values ?? [1]));
  const source = view?.weights.source;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Objectifs')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans k='Le budget du magasin, mois par mois — <1>un montant HT</1>, réparti sur ses jours selon leur poids.'
              parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </p>
        </div>
        <Link to="/stats/ventes?onglet=objectifs" className="oz-stats-export" style={BUTTON}><Icon name="trendUp" size={14} stroke={1.9} />{t('Voir le suivi')}</Link>
        <button type="button" onClick={exportYear} disabled={!view} title={t('Exporter les objectifs de l’année')} className="oz-stats-export"
          style={{ ...BUTTON, color: 'var(--ink-800)', opacity: view ? 1 : 0.55, cursor: view ? 'pointer' : 'default' }}>
          <Icon name="download" size={14} stroke={2} />{t('Export Excel')}
        </button>
      </div>

      {failure && <ReadFailed message={failure} />}
      {!view && !failure && <div className="dcard"><EmptyState icon="target" title={t('Lecture en cours…')} note={t('Les objectifs et le réalisé de l’exercice sont lus dans la base du magasin.')} /></div>}

      {view && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', padding: '10px 13px', marginBottom: 12 }}>
            <span style={MONO}>{t('Exercice')}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <button type="button" aria-label={t('Année précédente')} onClick={() => setYear(view.year - 1)} className="oz-stats-export"
                style={{ width: 30, height: 30, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>
                <Icon name="chevronL" size={14} stroke={2.2} />
              </button>
              <span style={{ minWidth: 54, textAlign: 'center', fontSize: 'var(--t-h4)', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{view.year}</span>
              <button type="button" aria-label={t('Année suivante')} onClick={() => setYear(view.year + 1)} className="oz-stats-export"
                style={{ width: 30, height: 30, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>
                <Icon name="chevronR" size={14} stroke={2.2} />
              </button>
            </span>
            {(view.undistributed.length > 0 || view.overcommitted.length > 0) && (
              <span role="note" style={{ marginLeft: 'auto', fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>
                {[
                  ...(view.undistributed.length ? [t('Sans aucun jour ouvert où se poser : {mois}.', { mois: view.undistributed.map((m) => moisLongLisible(m)).join(', ') })] : []),
                  ...(view.overcommitted.length ? [t('Jours fixés au-delà de l’objectif du mois : {mois}.', { mois: view.overcommitted.map((m) => moisLongLisible(m)).join(', ') })] : []),
                ].join(' ')}
              </span>
            )}
          </div>

          <div role="table" aria-label={t('Objectifs de l’exercice {annee}', { annee: String(view.year) })} style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
            <div role="row" style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, alignItems: 'center', padding: '9px 15px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
              <span role="columnheader" style={MONO}>{t('Mois')}</span>
              <span role="columnheader" style={{ ...MONO, textAlign: 'right' }}>{t('Objectif {symbole} HT', { symbole: currentCurrency().symbol })}</span>
              <span role="columnheader" style={MONO}>{t('Réalisé & atteinte')}</span>
              <span role="columnheader" style={{ ...MONO, textAlign: 'right' }}>{t('Réalisé')}</span>
              <span role="columnheader" style={{ ...MONO, textAlign: 'right' }}>{t('Détail du mois')}</span>
            </div>
            {view.months.map((m) => {
              const open = openMonth === m.month;
              const days = m.fixedDays ? t('{n} jour fixé', { n: m.fixedDays }) : t('Jours');
              return (
              <div key={m.month}>
              <div role="row" style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, alignItems: 'center', padding: '8px 15px', borderBottom: '1px solid var(--ink-100)' }}>
                <span role="rowheader" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>{monthLabel(m.month)}</span>
                  {m.state === 'current' && (
                    <span style={{ height: 19, display: 'inline-flex', alignItems: 'center', padding: '0 7px', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.04em', flexShrink: 0 }}>{t('EN COURS')}</span>
                  )}
                  {m.fromDays && (
                    <span style={{ height: 19, display: 'inline-flex', alignItems: 'center', padding: '0 7px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-micro)', fontWeight: 800, flexShrink: 0 }}>{t('somme des jours')}</span>
                  )}
                  {m.overCents !== null && (
                    <span style={{ height: 19, display: 'inline-flex', alignItems: 'center', padding: '0 7px', borderRadius: 'var(--r-pill)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-micro)', fontWeight: 800, flexShrink: 0 }}>{t('jours > objectif')}</span>
                  )}
                </span>
                <span role="cell">
                  {/* ⚠️ TOUS LES JOURS FIXÉS, LE MOIS NE SE SAISIT PLUS : sa somme s'affiche à la place du champ, et l'infobulle dit
                      comment le rouvrir — un champ en lecture seule ressemblerait à un champ qui refuse. */}
                  {m.fromDays ? (
                    <Tooltip display="block" content={t('Somme des jours fixés — rendez un jour au calcul pour saisir le mois')}>
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: 34, padding: '0 10px', boxSizing: 'border-box', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)', color: 'var(--ink-600)', fontSize: 'var(--t-lead)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        Σ {m.objectiveCents === null ? '—' : grouped(m.objectiveCents)}
                      </span>
                    </Tooltip>
                  ) : (
                    <CommitField value={amountInput(m.objectiveCents)} onCommit={commitMonth(m.month)} placeholder="—"
                      label={t('Objectif de {mois}', { mois: moisLongLisible(m.month) })}
                      style={{ ...FIELD, width: '100%', height: 34, padding: '0 10px', fontSize: 'var(--t-lead)', fontWeight: 700, textAlign: 'right' }} />
                  )}
                </span>
                <span role="cell" style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                  <span style={{ flex: 1, minWidth: 60, height: 7, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${m.rateBp === null ? 0 : Math.min(100, m.rateBp / 100)}%`, background: rateBar(m.rateBp), borderRadius: 'var(--r-pill)' }} />
                  </span>
                  <span style={{ minWidth: 44, textAlign: 'right', fontSize: 'var(--t-body)', fontWeight: 700, color: rateInk(m.rateBp), fontVariantNumeric: 'tabular-nums' }}>{pct0(m.rateBp)}</span>
                </span>
                <span role="cell" style={{ textAlign: 'right', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-700)', fontVariantNumeric: 'tabular-nums' }}>{eur0(m.realizedHtCents)}</span>
                <span role="cell" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" aria-expanded={open} onClick={() => setOpenMonth(open ? null : m.month)} className="oz-stats-export"
                    aria-label={`${days} · ${moisLongLisible(m.month)}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 28, padding: '0 10px', border: 'none', borderRadius: 'var(--r-pill)', background: m.fixedDays ? 'var(--teal-100)' : 'var(--ink-100)', color: m.fixedDays ? 'var(--teal-800)' : 'var(--ink-600)', fontSize: 'var(--t-meta)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                    <span style={{ display: 'flex', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}><Icon name="chevronR" size={12} stroke={2.4} /></span>
                    {days}
                  </button>
                </span>
              </div>
              {open && (
                <div role="row" style={{ padding: '12px 15px 14px 40px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-100)' }}>
                  <div role="cell" aria-label={t('Jours de {mois}', { mois: moisLongLisible(m.month) })}>
                    <MonthCalendar m={m} view={view} busy={busy} onCommit={commitDay} onUnfix={unfixDay} onUnfixMonth={() => void unfixMonth(m)} />
                  </div>
                </div>
              )}
              </div>
              );
            })}
            <div role="row" style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, alignItems: 'center', padding: '11px 15px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)' }}>
              <span role="rowheader" style={{ fontSize: 'var(--t-lead)', fontWeight: 800, color: 'var(--ink-900)' }}>{t('TOTAL {annee}', { annee: String(view.year) })}</span>
              <span role="cell" style={{ textAlign: 'right', fontSize: 'var(--t-control)', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{eur0(view.total.objectiveCents)}</span>
              <span role="cell" style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
                {view.total.months === 0 ? t('Aucun mois écoulé n’a d’objectif posé') : t('Sur {n} mois écoulé et budgété', { n: view.total.months })}
                {' · '}<strong style={{ color: rateInk(view.total.rateBp) }}>{pct0(view.total.rateBp)}</strong>
              </span>
              <span role="cell" style={{ textAlign: 'right', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-800)', fontVariantNumeric: 'tabular-nums' }}>{eur0(view.total.realizedHtCents)}</span>
              <span role="cell" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,360px),1fr))', gap: 13, marginTop: 14 }}>
            <div style={CARD}>
              <CardHead icon="chart" bg="var(--violet-bg)" fg="var(--violet-fg)" title={t('Le poids de chaque jour')}
                desc={t('L’objectif du mois se répartit selon ce poids — 100 = un jour moyen. Un samedi n’a pas la cible d’un lundi.')} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-note)', color: 'var(--ink-600)', flexWrap: 'wrap' }}>
                <span style={{ height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', display: 'inline-flex', alignItems: 'center', fontWeight: 700 }}>
                  {source === 'manual' ? t('Poids saisis à la main') : source === 'profile' ? t('Profil réel des 52 dernières semaines') : t('Aucune vente à lire : tous les jours ouverts valent 100')}
                </span>
                {source === 'manual' && (
                  <button type="button" disabled={busy} onClick={() => void gesture(() => api.setObjectiveWeights(null), t('Retour au profil réel'))} className="oz-stats-export"
                    style={{ height: 24, padding: '0 9px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--teal-800)', fontSize: 'var(--t-note)', fontWeight: 700, cursor: 'pointer' }}>
                    {t('Revenir au profil réel')}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {[1, 2, 3, 4, 5, 6, 7].map((wd) => {
                  const open = view.openDays.includes(wd);
                  const w = view.weights.values[wd - 1] ?? 0;
                  const ex = view.example?.byWeekday[wd - 1] ?? null;
                  const name = weekdayLabel(wd);
                  return (
                    <div key={wd} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--ink-100)' }}>
                      <span style={{ width: 78, flexShrink: 0, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>{name}</span>
                      {open ? (
                        <>
                          <span style={{ flex: 1, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
                            <span style={{ display: 'block', height: '100%', width: `${((w / maxWeight) * 100).toFixed(1)}%`, background: 'var(--violet)', borderRadius: 'var(--r-pill)', transition: 'width .3s' }} />
                          </span>
                          <CommitField value={String(w)} onCommit={commitWeight(wd - 1)} label={t('Poids du {jour}', { jour: name })}
                            style={{ ...FIELD, width: 58, textAlign: 'right' }} />
                          <span style={{ width: 150, flexShrink: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ex !== null && view.example ? t('{montant} HT un {jour} en {mois}', { montant: eur0(ex), jour: name.toLocaleLowerCase(currentLang()), mois: moisLongLisible(view.example.month) }) : ''}
                          </span>
                        </>
                      ) : (
                        <span style={{ flex: 1, fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>{t('Fermé (Réglages → Magasin)')}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 13, minWidth: 0 }}>
              <div style={CARD}>
                <CardHead icon="download" bg="var(--teal-100)" fg="var(--teal-800)" title={t('Pré-remplir l’exercice')}
                  desc={t('Partez du réalisé de l’an dernier et appliquez une progression — puis ajustez mois par mois.')} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-700)' }}>{t('Réalisé N-1')}</span>
                  <span style={{ position: 'relative', display: 'inline-flex' }}>
                    <input value={prefillText} onChange={(e) => setPrefillText(e.target.value)} inputMode="decimal" aria-label={t('Progression en %')}
                      style={{ ...FIELD, width: 72, textAlign: 'right', paddingRight: 22 }} />
                    <span aria-hidden style={{ position: 'absolute', right: 8, top: 8, fontSize: 'var(--t-dense)', color: 'var(--ink-400)', pointerEvents: 'none' }}>%</span>
                  </span>
                  <button type="button" disabled={busy} onClick={() => void prefill()} className="oz-stats-close"
                    style={{ height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-body)', fontWeight: 700, cursor: 'pointer' }}>
                    {t('Pré-remplir')}
                  </button>
                  <button type="button" disabled={busy} onClick={() => void copy()} className="oz-stats-export"
                    style={{ ...BUTTON, height: 32, padding: '0 12px' }}>{t('Recopier l’exercice précédent')}</button>
                </div>
              </div>

              <div style={CARD}>
                <CardHead icon="calendar" bg="var(--amber-bg)" fg="var(--amber-fg)" title={t('Jours exceptionnels')}
                  desc={t('Une fermeture ou un événement change la cible du jour — l’objectif du mois, lui, ne bouge pas.')} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <input type="date" value={exDay} onChange={(e) => setExDay(e.target.value)} aria-label={t('Date du jour exceptionnel')} style={FIELD} />
                  <Seg label={t('Nature du jour exceptionnel')} value={exKind} onChange={setExKind}
                    options={[{ v: 'event', l: t('Événement') }, { v: 'closed', l: t('Fermeture') }]} />
                  {exKind === 'event' && (
                    <span style={{ position: 'relative', display: 'inline-flex' }}>
                      <input value={exText} onChange={(e) => setExText(e.target.value)} inputMode="numeric" aria-label={t('Variation en %')}
                        style={{ ...FIELD, width: 70, textAlign: 'right', paddingRight: 22 }} />
                      <span aria-hidden style={{ position: 'absolute', right: 8, top: 8, fontSize: 'var(--t-dense)', color: 'var(--ink-400)', pointerEvents: 'none' }}>%</span>
                    </span>
                  )}
                  <button type="button" disabled={busy} onClick={() => void addException()}
                    style={{ height: 32, padding: '0 12px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-body)', fontWeight: 700, cursor: 'pointer' }}>
                    {t('Ajouter')}
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {view.exceptions.map((x) => {
                    const shownDay = formatDate(dateOfYmd(x.day));
                    const closed = x.kind === 'closed';
                    return (
                      <div key={x.day} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: '1px solid var(--ink-100)' }}>
                        <span style={{ width: 84, fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{shownDay}</span>
                        <span style={{ height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', background: closed ? 'var(--rose-bg)' : 'var(--violet-bg)', color: closed ? 'var(--rose-fg)' : 'var(--violet-fg)', fontSize: 'var(--t-meta)', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>
                          {closed ? t('Fermeture exceptionnelle') : t('Événement {variation}', { variation: dTx(x.changeBp) })}
                        </span>
                        <button type="button" disabled={busy} aria-label={t('Retirer le {date}', { date: shownDay })} title={t('Retirer ce jour')}
                          onClick={() => void gesture(() => api.removeObjectiveDay(x.day), t('Jour exceptionnel retiré'))}
                          style={{ marginLeft: 'auto', width: 26, height: 26, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <Icon name="x" size={12} stroke={2.4} />
                        </button>
                      </div>
                    );
                  })}
                  {view.exceptions.length === 0 && <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>{t('Aucun jour exceptionnel.')}</span>}
                </div>
              </div>
            </div>

            <div style={{ ...CARD, gridColumn: '1 / -1' }}>
              <CardHead icon="history" bg="var(--blue-bg)" fg="var(--blue-fg)" title={t('Historique des modifications')}
                desc={t('Qui a changé une cible, et quand — utile quand un mois est déjà commencé.')} />
              {view.history.map((h, i) => (
                <div key={`${h.at}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-body)' }}>
                  <span style={{ width: 118, flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{dateTime(new Date(h.at))}</span>
                  <span style={{ width: 140, flexShrink: 0, fontWeight: 700, color: 'var(--ink-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.actor}</span>
                  <span style={{ flex: 1, minWidth: 0, color: 'var(--ink-700)' }}>{objectiveChangeText(h)}</span>
                </div>
              ))}
              {view.history.length === 0 && <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>{t('Aucune modification pour l’instant.')}</span>}
            </div>
          </div>
        </>
      )}
    </>
  );
}
